import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import {
  Button,
  Form,
  Input,
  message,
  Modal,
  Popconfirm,
  Select,
  Table,
  Space,
  Typography,
  Descriptions,
} from "antd";
import {
  DeleteOutlined,
  EditOutlined,
  FileTextOutlined,
  CloseCircleOutlined,
  EyeOutlined,
} from "@ant-design/icons";
import axios from "axios";
import { cloudFrontUrl, serverUrl } from "../../../nestserver";
import { uploadDoc } from "../../../services/upload.service";

const { Title, Text } = Typography;
const { Option } = Select;

export default function RazorpayKyc() {
  const { id: schoolId } = useParams();
  const [form] = Form.useForm();

  const token = window.localStorage.getItem("jwt-token");
  const config = useMemo(
    () => ({ headers: { Authorization: `Bearer ${token}` } }),
    [token],
  );

  const [loading, setLoading] = useState(false);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [kycData, setKycData] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [isViewModalVisible, setIsViewModalVisible] = useState(false);
  const [viewingRecord, setViewingRecord] = useState(null);

  const [panDocUrl, setPanDocUrl] = useState("");
  const [gstDocUrl, setGstDocUrl] = useState("");
  const [bankProofUrl, setBankProofUrl] = useState("");
  const [businessProofUrl, setBusinessProofUrl] = useState("");

  const businessTypes = [
    "proprietary",
    "partnership",
    "private_limited",
    "public_limited",
    "llp",
    "trust",
    "society",
    "npo",
    "huf",
  ];

  const fetchKycDetails = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(
        `${serverUrl}/razorpay-kyc/school/${schoolId}`,
        config,
      );
      console.log("Razorpay KYC Response:", res.data);

      const rawData = res.data?.result?.data || res.data?.data || res.data;

      if (rawData && (Array.isArray(rawData) || rawData._id)) {
        const data = Array.isArray(rawData) ? rawData : [rawData];
        setKycData(data.filter(Boolean));
      } else {
        setKycData([]);
      }
    } catch (error) {
      console.error("KYC fetching error:", error);
      setKycData([]);
    } finally {
      setLoading(false);
    }
  }, [schoolId, config]);

  useEffect(() => {
    if (schoolId && token) {
      fetchKycDetails();
    }
  }, [schoolId, token, fetchKycDetails]);

  const handleFileUpload = async (event, type) => {
    const file = event.target.files[0];
    if (!file) return;

    const hide = message.loading("Uploading document...", 0);
    try {
      const response = await uploadDoc({ file }, "razorpay-kyc");
      // Pick first element if it's an array
      const fileData = Array.isArray(response) ? response[0] : response;
      const fileId = fileData?.id || fileData?.filename;

      if (fileId) {
        if (type === "pan") setPanDocUrl(fileId);
        if (type === "gst") setGstDocUrl(fileId);
        if (type === "bank") setBankProofUrl(fileId);
        if (type === "business") setBusinessProofUrl(fileId);
        message.success("Document uploaded securely");
      } else {
        console.error("Upload response unexpected:", response);
        message.error("Upload failed: No identifier returned");
      }
    } catch (error) {
      console.error(error);
      message.error("Upload failed");
    } finally {
      hide();
    }
  };

  const onFinish = async (values) => {
    setLoading(true);
    try {
      const payload = {
        school: schoolId,
        ...values,
        pan_document_url: panDocUrl,
        gst_document_url: gstDocUrl,
        bank_proof_url: bankProofUrl,
        business_proof_url: businessProofUrl,
        status: values.status || "pending",
      };

      if (editingId) {
        await axios.post(`${serverUrl}/razorpay-kyc`, payload, config);
        message.success("KYC Details updated successfully!");
      } else {
        await axios.post(`${serverUrl}/razorpay-kyc`, payload, config);
        message.success("KYC Details saved successfully!");
      }

      setIsModalVisible(false);
      resetModal();
      fetchKycDetails();
    } catch (error) {
      message.error(
        error?.response?.data?.message || "Something went wrong saving KYC",
      );
    } finally {
      setLoading(false);
    }
  };

  const resetModal = () => {
    form.resetFields();
    setEditingId(null);
    setPanDocUrl("");
    setGstDocUrl("");
    setBankProofUrl("");
    setBusinessProofUrl("");
  };

  const handleDelete = async (id) => {
    const hide = message.loading("Deleting KYC...", 0);
    try {
      await axios.delete(`${serverUrl}/razorpay-kyc/${id}`, config);
      message.success("KYC record deleted");
      fetchKycDetails();
    } catch (error) {
      message.error("Failed to delete KYC record");
    } finally {
      hide();
    }
  };

  const showEditModal = (record) => {
    setEditingId(record._id);
    form.setFieldsValue({
      business_type: record.business_type,
      pan_number: record.pan_number,
      gstin: record.gstin,
      beneficiary_name: record.beneficiary_name,
      account_number: record.account_number,
      ifsc_code: record.ifsc_code,
      status: record.status,
    });
    setPanDocUrl(record.pan_document_url || "");
    setGstDocUrl(record.gst_document_url || "");
    setBankProofUrl(record.bank_proof_url || "");
    setBusinessProofUrl(record.business_proof_url || "");
    setIsModalVisible(true);
  };

  const columns = [
    {
      title: "Business Type",
      dataIndex: "business_type",
      key: "business_type",
      align: "center",
      render: (text) => (
        <span className="uppercase">{text?.replace(/_/g, " ")}</span>
      ),
    },
    {
      title: "PAN",
      dataIndex: "pan_number",
      key: "pan_number",
      align: "center",
      render: (text) => <span className="uppercase">{text}</span>,
    },
    {
      title: "Beneficiary",
      dataIndex: "beneficiary_name",
      key: "beneficiary_name",
      align: "center",
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      align: "center",
      render: (status) => (
        <span
          className={`px-2 py-1 rounded-full text-xs font-black capitalize ${status === "completed" ? "text-green-700" : "text-yellow-700"}`}
        >
          {status}
        </span>
      ),
    },
    {
      title: "Docs",
      key: "docs",
      align: "center",
      render: (_, record) => (
        <Space>
          {record.pan_document_url && (
            <a
              href={`${cloudFrontUrl}/razorpay-kyc/${record.pan_document_url}`}
              target="_blank"
              rel="noreferrer"
            >
              <FileTextOutlined
                title="PAN Proof"
                className="text-gray-500 text-lg"
              />
            </a>
          )}
          {record.gst_document_url && (
            <a
              href={`${cloudFrontUrl}/razorpay-kyc/${record.gst_document_url}`}
              target="_blank"
              rel="noreferrer"
            >
              <FileTextOutlined
                style={{ color: "blue" }}
                title="GST Proof"
                className="text-lg"
              />
            </a>
          )}
          {record.bank_proof_url && (
            <a
              href={`${cloudFrontUrl}/razorpay-kyc/${record.bank_proof_url}`}
              target="_blank"
              rel="noreferrer"
            >
              <FileTextOutlined
                style={{ color: "green" }}
                title="Bank Proof"
                className="text-lg"
              />
            </a>
          )}
          {record.business_proof_url && (
            <a
              href={`${cloudFrontUrl}/razorpay-kyc/${record.business_proof_url}`}
              target="_blank"
              rel="noreferrer"
            >
              <FileTextOutlined
                style={{ color: "orange" }}
                title="Business Proof"
                className="text-lg"
              />
            </a>
          )}
        </Space>
      ),
    },
    {
      title: "Action",
      key: "action",
      align: "center",
      render: (_, record) => (
        <Space size="middle">
          <Button
            icon={<EyeOutlined />}
            onClick={() => {
              setViewingRecord(record);
              setIsViewModalVisible(true);
            }}
            type="link"
            size="small"
            className="flex items-center"
          >
            View
          </Button>
          <Button
            icon={<EditOutlined />}
            onClick={() => showEditModal(record)}
            type="link"
            size="small"
            className="flex items-center py-4"
          >
            Edit
          </Button>
          <Popconfirm
            title="Are you sure to delete this KYC?"
            onConfirm={() => handleDelete(record._id)}
            okText="Yes"
            cancelText="No"
          >
            <Button
              icon={<DeleteOutlined />}
              danger
              type="link"
              size="small"
              className="flex items-center"
            >
              Delete
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  if (!schoolId) {
    return (
      <div className="flex justify-center items-center h-48 bg-gray-50 rounded-lg">
        <Text type="secondary">
          {" "}
          Please open or select a school from the list first to manage its
          KYC.{" "}
        </Text>
      </div>
    );
  }

  return (
    <div>
      <div className="mt-8">
        <div
          style={{ borderRadius: "10px", color: "#616161" }}
          className=" bg-white drop-shadow-md p-8 w-full "
        >
          <div className="flex justify-between items-center mb-6">
            <div>
              <div className="text-2xl text-orange-500 font-semibold">
                Razorpay Sub-Merchant KYC
              </div>
              <Text type="secondary" size="small">
                Manage onboarding details for manual Razorpay Linked Account
                creation.
              </Text>
            </div>
            <Button
              type="primary"
              size="large"
              style={{ padding: "4px 16px" }}
              onClick={() => {
                resetModal();
                setIsModalVisible(true);
              }}
            >
              Add KYC Details
            </Button>
          </div>

          <Table
            columns={columns}
            dataSource={kycData}
            rowKey="_id"
            loading={loading}
            pagination={false}
            size="small"
            rowClassName="hover:bg-gray-50"
            locale={{ emptyText: "No KYC details found for this school." }}
          />
        </div>
      </div>

      <Modal
        title={editingId ? "Update KYC Details" : "Add KYC Details"}
        visible={isModalVisible}
        onCancel={() => setIsModalVisible(false)}
        footer={null}
        width={800}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={onFinish}
          autoComplete="off"
          initialValues={{ status: "pending" }}
        >
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-4">
            <Form.Item
              name="business_type"
              label="Business Type"
              rules={[
                { required: true, message: "Please select business type!" },
              ]}
            >
              <Select placeholder="Select Type">
                {businessTypes.map((t) => (
                  <Option key={t} value={t}>
                    {t.replace(/_/g, " ").toUpperCase()}
                  </Option>
                ))}
              </Select>
            </Form.Item>
            <Form.Item
              name="pan_number"
              label="PAN Number"
              rules={[{ required: true, message: "Please input PAN number!" }]}
            >
              <Input placeholder="ABCDE1234F" className="uppercase" />
            </Form.Item>
            <Form.Item name="gstin" label="GSTIN (Optional)">
              <Input placeholder="22AAAAA0000A1Z5" className="uppercase" />
            </Form.Item>
            <Form.Item
              name="beneficiary_name"
              label="Beneficiary Name"
              rules={[
                { required: true, message: "Please input beneficiary name!" },
              ]}
            >
              <Input placeholder="Exact name on bank account" />
            </Form.Item>
            <Form.Item
              name="account_number"
              label="Account Number"
              rules={[
                { required: true, message: "Please input account number!" },
              ]}
            >
              <Input />
            </Form.Item>
            <Form.Item
              name="ifsc_code"
              label="IFSC Code"
              rules={[{ required: true, message: "Please input IFSC code!" }]}
            >
              <Input placeholder="HDFC0001234" className="uppercase" />
            </Form.Item>
            <Form.Item name="status" label="Collection Status">
              <Select>
                <Option value="pending">Pending</Option>
                <Option value="completed">Completed</Option>
              </Select>
            </Form.Item>
          </div>

          <div className="mt-4 border-t pt-4">
            <Title level={5}>Proof Documents</Title>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <div className="flex flex-col gap-1">
                <Text type="secondary" size="small">
                  PAN Document
                </Text>
                {panDocUrl ? (
                  <Space>
                    <Text type="success" ellipsis>
                      <a
                        href={`${cloudFrontUrl}/razorpay-kyc/${panDocUrl}`}
                        target="_blank"
                        rel="noreferrer"
                      >
                        Uploaded ✓
                      </a>
                    </Text>
                    <CloseCircleOutlined
                      className="text-red-500 cursor-pointer"
                      onClick={() => setPanDocUrl("")}
                    />
                  </Space>
                ) : (
                  <Input
                    type="file"
                    onChange={(e) => handleFileUpload(e, "pan")}
                  />
                )}
              </div>
              <div className="flex flex-col gap-1">
                <Text type="secondary" size="small">
                  GST Document
                </Text>
                {gstDocUrl ? (
                  <Space>
                    <Text type="success" ellipsis>
                      <a
                        href={`${cloudFrontUrl}/razorpay-kyc/${gstDocUrl}`}
                        target="_blank"
                        rel="noreferrer"
                      >
                        Uploaded ✓
                      </a>
                    </Text>
                    <CloseCircleOutlined
                      className="text-red-500 cursor-pointer"
                      onClick={() => setGstDocUrl("")}
                    />
                  </Space>
                ) : (
                  <Input
                    type="file"
                    onChange={(e) => handleFileUpload(e, "gst")}
                  />
                )}
              </div>
              <div className="flex flex-col gap-1">
                <Text type="secondary" size="small">
                  Bank Proof
                </Text>
                {bankProofUrl ? (
                  <Space>
                    <Text type="success" ellipsis>
                      <a
                        href={`${cloudFrontUrl}/razorpay-kyc/${bankProofUrl}`}
                        target="_blank"
                        rel="noreferrer"
                      >
                        Uploaded ✓
                      </a>
                    </Text>
                    <CloseCircleOutlined
                      className="text-red-500 cursor-pointer"
                      onClick={() => setBankProofUrl("")}
                    />
                  </Space>
                ) : (
                  <Input
                    type="file"
                    onChange={(e) => handleFileUpload(e, "bank")}
                  />
                )}
              </div>
              <div className="flex flex-col gap-1">
                <Text type="secondary" size="small">
                  Business Proof
                </Text>
                {businessProofUrl ? (
                  <Space>
                    <Text type="success" ellipsis>
                      <a
                        href={`${cloudFrontUrl}/razorpay-kyc/${businessProofUrl}`}
                        target="_blank"
                        rel="noreferrer"
                      >
                        Uploaded ✓
                      </a>
                    </Text>
                    <CloseCircleOutlined
                      className="text-red-500 cursor-pointer"
                      onClick={() => setBusinessProofUrl("")}
                    />
                  </Space>
                ) : (
                  <Input
                    type="file"
                    onChange={(e) => handleFileUpload(e, "business")}
                  />
                )}
              </div>
            </div>
            <div className="mt-2">
              <Button
                type="link"
                size="small"
                onClick={() => {
                  setPanDocUrl("");
                  setGstDocUrl("");
                  setBankProofUrl("");
                  setBusinessProofUrl("");
                }}
              >
                {" "}
                Clear All Uploads{" "}
              </Button>
            </div>
          </div>

          <div className="flex justify-end gap-x-4 mt-8">
            <Button onClick={() => setIsModalVisible(false)}>Cancel</Button>
            <Button type="primary" htmlType="submit" loading={loading}>
              {editingId ? "Update Details" : "Save Details"}
            </Button>
          </div>
        </Form>
      </Modal>
      <Modal
        title="KYC Details View"
        visible={isViewModalVisible}
        onCancel={() => setIsViewModalVisible(false)}
        footer={[
          <Button key="close" onClick={() => setIsViewModalVisible(false)}>
            Close
          </Button>,
        ]}
        width={700}
      >
        {viewingRecord && (
          <Descriptions bordered column={1} size="small">
            <Descriptions.Item label="Business Type">
              <span className="uppercase">
                {viewingRecord.business_type?.replace(/_/g, " ")}
              </span>
            </Descriptions.Item>
            <Descriptions.Item label="PAN Number">
              {viewingRecord.pan_number?.toUpperCase()}
            </Descriptions.Item>
            <Descriptions.Item label="GSTIN">
              {viewingRecord.gstin?.toUpperCase() || "N/A"}
            </Descriptions.Item>
            <Descriptions.Item label="Beneficiary Name">
              {viewingRecord.beneficiary_name}
            </Descriptions.Item>
            <Descriptions.Item label="Account Number">
              {viewingRecord.account_number}
            </Descriptions.Item>
            <Descriptions.Item label="IFSC Code">
              {viewingRecord.ifsc_code?.toUpperCase()}
            </Descriptions.Item>
            <Descriptions.Item label="Status">
              <span
                className={`font-bold capitalize ${viewingRecord.status === "completed" ? "text-green-600" : "text-yellow-600"}`}
              >
                {viewingRecord.status}
              </span>
            </Descriptions.Item>
            <Descriptions.Item label="Documents">
              <Space direction="vertical">
                {viewingRecord.pan_document_url && (
                  <a
                    href={`${cloudFrontUrl}/razorpay-kyc/${viewingRecord.pan_document_url}`}
                    target="_blank"
                    rel="noreferrer"
                  >
                    PAN Proof ✓
                  </a>
                )}
                {viewingRecord.gst_document_url && (
                  <a
                    href={`${cloudFrontUrl}/razorpay-kyc/${viewingRecord.gst_document_url}`}
                    target="_blank"
                    rel="noreferrer"
                  >
                    GST Proof ✓
                  </a>
                )}
                {viewingRecord.bank_proof_url && (
                  <a
                    href={`${cloudFrontUrl}/razorpay-kyc/${viewingRecord.bank_proof_url}`}
                    target="_blank"
                    rel="noreferrer"
                  >
                    Bank Proof ✓
                  </a>
                )}
                {viewingRecord.business_proof_url && (
                  <a
                    href={`${cloudFrontUrl}/razorpay-kyc/${viewingRecord.business_proof_url}`}
                    target="_blank"
                    rel="noreferrer"
                  >
                    Business Proof ✓
                  </a>
                )}
              </Space>
            </Descriptions.Item>
          </Descriptions>
        )}
      </Modal>
    </div>
  );
}
